import React, { useState } from 'react';
interface FallbackProps {
  title?: string;
  description?: string;
  image_urls?: string[] | string;
  url?: string;
  type?: string;
  result?: any;
}

const Fallback: React.FC<FallbackProps> = (props) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Convert string image_url to array if needed
  let imageUrls: string[] | undefined;
  if (typeof props.image_urls === 'string') {
          // Check if it's a string representation of an array (starts with [ and ends with ])
      if (props.image_urls.startsWith('[') && props.image_urls.endsWith(']')) {
        console.log('image_urls is a string representation of an array', props.image_urls);
        try {
          // Replace single quotes with double quotes to make it valid JSON
          const jsonString = props.image_urls.replace(/'/g, '"');
          // Try to parse it as JSON
          imageUrls = JSON.parse(jsonString);
          console.log('imageUrls', imageUrls);
        } catch {
          console.log('imageUrls is a single URL', props.image_urls);
          // If JSON parsing fails, treat it as a single URL
          imageUrls = [props.image_urls];
        }
    } else {
      // Single URL string
      imageUrls = [props.image_urls];
    }
  } else {
    // Already an array
    imageUrls = props.image_urls;
  }
  
  const hasValidImages = imageUrls && imageUrls.length > 0 && !(imageUrls.length === 1 && imageUrls[0] === '#');
  const hasTitleAndImages = props.title && hasValidImages;
  const hasBasicDisplayData = props.title || props.description || hasValidImages || props.url;
  const shouldShowJson = !hasBasicDisplayData;

  const handlePreviousImage = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentImageIndex((prev) => (prev === 0 ? (imageUrls?.length || 0) - 1 : prev - 1));
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentImageIndex((prev) => (prev === (imageUrls?.length || 0) - 1 ? 0 : prev + 1));
  };

  // If no basic display data, show JSON view
  if (shouldShowJson) {
    return (
      <pre className="text-sm text-[var(--rhea-text-secondary)] p-4">
        {JSON.stringify(props.result, null, 2)}
      </pre>
    );
  }

  const Content = () => (
    <>
      {hasValidImages && (
        <div className="min-h-0 min-w-0">
          <img
            src={imageUrls![currentImageIndex]}
            alt={props.title}
            className="max-h-full max-w-full object-contain"
          />
          {imageUrls!.length > 1 && (
            <>
              <button
                onClick={handlePreviousImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                aria-label="Previous image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                aria-label="Next image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      )}
      
      <div className={`${hasValidImages ? 'p-3' : 'p-4'} w-full`}>
        <h3 className={`font-semibold mb-1 ${hasValidImages ? 'text-sm' : 'text-lg'} truncate`} style={{ color: 'var(--rhea-text-primary)' }}>
          {props.title}
        </h3>
        {props.description && (
          <p className={`${hasValidImages ? 'text-xs line-clamp-2' : 'text-sm line-clamp-5'}`} style={{ color: 'var(--rhea-text-secondary)' }}>
            {props.description}
          </p>
        )}
        {!props.description && props.result?.content && (
          <p className={`${hasValidImages ? 'text-xs line-clamp-2' : 'text-sm line-clamp-5'}`} style={{ color: 'var(--rhea-text-secondary)' }}>
            {props.result.content}
          </p>
        )}
      </div>
    </>
  );

  // Render a proper card with image carousel, title, description, and click functionality
  return (
    <div 
      className="group rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 w-full h-full border"
      style={{ backgroundColor: 'var(--rhea-card-bg)', borderColor: 'var(--rhea-card-border)' }}
    >
      {props.url ? (
        <a href={props.url} target="_blank" rel="noopener noreferrer" className="w-full h-full flex flex-col flex-auto items-center justify-center">
          <Content />
        </a>
      ) : (
        <div className="w-full h-full flex flex-col flex-auto items-center justify-center">
          <Content />
        </div>
      )}
    </div>
  );
};

export default Fallback; 